import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { BASE_URL } from '../../utils/api';

const formSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  details: z.string().min(1, 'Details are required'),
  tags: z.string().optional(),
  image: z.any().optional(),
  category: z.string().optional(),
});

const categoryTagMap = {
  'Django': ['django', 'python', 'web'],
  'Python': ['python', 'pandas', 'numpy'],
  'Java': ['java', 'spring', 'jvm'],
  'SQL': ['sql', 'queries', 'database'],
  'Cloud': ['Cloud','aws', 'azure', 'google cloud'],
  'React.js': ['react.js', 'javascript', 'frontend'],
  'Web Development': ['html', 'css', 'javascript'],
  'Data Science': ['python', 'pandas', 'statistics'],
  'Machine Learning': ['ml', 'tensorflow', 'ai'],
  'Cybersecurity': ['security', 'network', 'encryption'],
  'DevOps': ['docker', 'kubernetes', 'ci/cd'],
  'UI/UX Design': ['ui', 'ux', 'design'],
  'Mobile App Development': ['flutter', 'android', 'ios'],
  'Big Data': ['hadoop', 'spark', 'kafka'],
  'Artificial Intelligence': ['ai', 'nlp', 'neural networks'],
  'Blockchain': ['blockchain', 'ethereum', 'smart contracts'],
  'Internet of Things': ['iot', 'sensors', 'arduino'],
  'Game Development': ['unity', 'c#', 'graphics'],
  'Testing & QA': ['testing', 'selenium', 'automation'],
  'C': ['c language', 'pointers', 'functions'],
  'C++': ['c++', 'oop', 'stl'],
  'Node.js': ['node.js', 'express', 'javascript'],
  'Data Analyst': ['excel', 'sql', 'powerbi'],
  'Embedded Systems': ['microcontroller', 'embedded c', 'firmware'],
  'Software Engineering': ['design patterns', 'sdlc', 'architecture'],
  'AR/VR Development': ['unity', 'arcore', 'vr'],
  'Gen AI': ['generative ai', 'llm', 'prompt engineering'],
  'Agent AI': ['autonomous agents', 'llm', 'planning'],
  'Reporting Tools': ['powerbi', 'tableau', 'dashboards'],
  'Others': []
};

const AskQuestion = () => {
  const navigate = useNavigate();
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      details: '',
      tags: '',
      image: undefined,
      category: '',
    },
  });

  const [message, setMessage] = useState({ text: '', type: '' });
  const [previewMode, setPreviewMode] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  // Load draft on mount
  useEffect(() => {
    const savedDraft = localStorage.getItem('questionDraft');
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      navigate('/login');
    }
    if (savedDraft) {
      const draft = JSON.parse(savedDraft);
      form.setValue('title', draft.title || '');
      form.setValue('category', draft.category || '');
      form.setValue('tags', draft.tags || '');
      form.setValue('details', draft.details || '');
    }
  }, []);

  // Handle category change and auto-tagging
  const handleCategoryChange = (e) => {
    const selectedCategory = e.target.value;
    form.setValue('category', selectedCategory);
    const autoTags = categoryTagMap[selectedCategory];
    form.setValue('tags', autoTags ? autoTags.join(', ') : '');
  };

  // Handle image upload and preview
  const handleImageUpload = async (file, setValue) => {
    if (!file || file.size > 5 * 1024 * 1024) return;
    setImagePreview(URL.createObjectURL(file));
    // Optionally upload to server here, or just preview
    // ... existing upload logic ...
    // For now, just preview
  };

  // Remove image
  const handleImageRemove = () => {
    setImagePreview(null);
    form.setValue('image', undefined);
  };

  // Save draft
  const handleSaveDraft = () => {
    const draft = {
      title: form.getValues('title'),
      category: form.getValues('category'),
      tags: form.getValues('tags'),
      details: form.getValues('details'),
    };
    localStorage.setItem('questionDraft', JSON.stringify(draft));
    setMessage({ text: 'Draft saved successfully!', type: 'success' });
  };

  // Post question (with image upload if needed)
  const onSubmit = async (values) => {
    if (!values.title.trim() || !values.details.trim() || !values.category) {
      setMessage({ text: 'Please fill in the title, details, and select a category.', type: 'error' });
      return;
    }
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      navigate('/login');
      return;
    }
    const formData = new FormData();
    formData.append('title', values.title);
    formData.append('details', values.details);
    formData.append('category', values.category);
    const tagList = values.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
    formData.append('tags', tagList.join(', '));
    if (form.getValues('image')) {
      formData.append('image', form.getValues('image'));
    }
    try {
      const response = await fetch(`${BASE_URL}/questions/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        body: formData,
      });
      if (response.ok) {
        setMessage({ text: 'Question posted successfully!', type: 'success' });
        localStorage.removeItem('questionDraft');
        navigate('/dashboard');
      } else {
        const errorData = await response.json();
        setMessage({ text: errorData.detail || 'Failed to post question. Please check the inputs and try again.', type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'An error occurred. Please try again.', type: 'error' });
    }
  };

  // Preview content
  const previewContent = (
    <div className="bg-gradient-to-br from-brand-light to-accent2/20 border-l-4 border-brand p-6 rounded-xl shadow-lg mb-6">
      <h3 className="text-xl font-bold mb-2 text-brand-dark">{form.getValues('title')}</h3>
      <p className="mb-1 text-teal"><strong>Category:</strong> {form.getValues('category')}</p>
      <p className="mb-1 text-accent2"><strong>Tags:</strong> {form.getValues('tags')}</p>
      <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: form.getValues('details') }} />
      {imagePreview && <img src={imagePreview} alt="Preview" className="mt-4 max-w-xs rounded border border-brand" />}
    </div>
  );

  return (
    <div className="container mx-auto py-10 flex justify-center items-center min-h-[90vh] bg-gradient-to-br from-brand-light via-white to-accent2/10">
      <Card className="w-full max-w-2xl shadow-xl border border-brand/30 rounded-2xl bg-white/90">
        <CardHeader className="pb-2 border-b border-brand/20 bg-brand/10 rounded-t-2xl">
          <CardTitle className="text-2xl font-bold text-brand-dark">Ask a Question</CardTitle>
        </CardHeader>
        <CardContent>
          {previewMode ? (
            <>
              {previewContent}
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" className="border-brand text-brand hover:bg-brand-light" onClick={() => setPreviewMode(false)}>Edit</Button>
                <Button type="button" className="px-6 py-2 rounded-lg text-base font-semibold shadow bg-gradient-to-r from-brand to-accent2 text-white hover:from-brand-dark hover:to-accent2/80 transition-colors" onClick={form.handleSubmit(onSubmit)}>Post Question</Button>
              </div>
            </>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-7">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-semibold text-brand">Title</FormLabel>
                      <FormControl>
                        <Input placeholder="What's your question? Be specific." {...field} className="bg-brand-light/50 border-brand/30 focus:border-brand focus:ring-2 focus:ring-brand/30 transition" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-semibold text-teal">Category</FormLabel>
                      <FormControl>
                        <select
                          className="w-full border border-teal rounded-md p-2 bg-teal/10 focus:border-teal focus:ring-2 focus:ring-teal/20 transition"
                          value={field.value}
                          onChange={e => {
                            field.onChange(e);
                            handleCategoryChange(e);
                          }}
                        >
                          <option value="">Select a category</option>
                          {Object.keys(categoryTagMap).map((cat) => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="tags"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-semibold text-accent2">Tags</FormLabel>
                      <FormControl>
                        <Input placeholder="Add tags separated by commas (e.g., javascript, react, node)" {...field} className="bg-accent2/10 border-accent2/30 focus:border-accent2 focus:ring-2 focus:ring-accent2/20 transition" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="details"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-semibold text-brand">Details</FormLabel>
                      <FormControl>
                        <RichTextEditor
                          content={field.value}
                          onChange={field.onChange}
                          placeholder="Provide all the details someone would need to answer your question..."
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="image"
                  render={({ field: { onChange } }) => (
                    <FormItem>
                      <FormLabel className="text-base font-semibold text-accent2">Upload Image</FormLabel>
                      <FormControl>
                        <div className="flex flex-col gap-2">
                          <Input
                            type="file"
                            accept="image/*"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (file) {
                                await handleImageUpload(file, (detailsSetter) => form.setValue('details', detailsSetter(form.getValues('details'))));
                                form.setValue('image', file);
                              }
                            }}
                            className="cursor-pointer bg-accent2/10 border-accent2/30 focus:border-accent2 focus:ring-2 focus:ring-accent2/20 transition"
                          />
                          {imagePreview && (
                            <div className="flex items-center gap-4 mt-2">
                              <img src={imagePreview} alt="Preview" className="max-h-24 rounded shadow border border-accent2" />
                              <Button type="button" variant="destructive" onClick={handleImageRemove}>Remove</Button>
                            </div>
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {message.text && (
                  <div className={`p-4 rounded-md text-base font-medium ${
                    message.type === 'error' ? 'bg-destructive/10 text-destructive' : 'bg-teal/10 text-teal'
                  }`}>
                    {message.text}
                  </div>
                )}
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" className="border-brand text-brand hover:bg-brand-light" onClick={() => setPreviewMode(true)}>Preview</Button>
                  <Button type="button" variant="secondary" className="bg-teal/10 text-teal hover:bg-teal/20" onClick={handleSaveDraft}>Save Draft</Button>
                  <Button type="submit" className="px-6 py-2 rounded-lg text-base font-semibold shadow bg-gradient-to-r from-brand to-accent2 text-white hover:from-brand-dark hover:to-accent2/80 transition-colors">Post Question</Button>
                </div>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AskQuestion;